import coastalImage from '@/assets/themes/coastal-architecture.jpeg';
import morningImage from '@/assets/themes/morning-still-life.jpeg';
import {useEffect} from 'react';
import type {CSSProperties} from 'react';
import type {ThemeBaseId, ThemeSetupInput} from '@/api/theme-builder';

type Preset = NonNullable<ThemeSetupInput['preset']>;

const layouts: Record<ThemeBaseId, {name: string; description: string}> = {
    source: {name: 'Source', description: 'Editorial'},
    casper: {name: 'Casper', description: 'Magazine'},
    edition: {name: 'Edition', description: 'Newsletter'}
};

const presets: Record<Preset | 'default', {accent: string; background: string; surface: string; text: string; muted: string; heading: string; body: string}> = {
    default: {accent: '#15171a', background: '#ffffff', surface: '#f5f5f2', text: '#15171a', muted: '#626975', heading: 'Inter', body: 'Inter'},
    editorial: {accent: '#344f6a', background: '#ffffff', surface: '#f4f3ef', text: '#202b36', muted: '#4d5964', heading: 'Lora', body: 'Source Sans 3'},
    minimal: {accent: '#252525', background: '#ffffff', surface: '#f5f5f5', text: '#202020', muted: '#555555', heading: 'Inter', body: 'Inter'},
    bold: {accent: '#802f46', background: '#ffffff', surface: '#f8f2f3', text: '#281f24', muted: '#594d52', heading: 'Manrope', body: 'Inter'},
    warm: {accent: '#8c4931', background: '#fffdf8', surface: '#f6eee2', text: '#352820', muted: '#665249', heading: 'EB Garamond', body: 'Source Sans 3'},
    technical: {accent: '#245a75', background: '#ffffff', surface: '#eef4f6', text: '#1a2d38', muted: '#4b626d', heading: 'Source Sans 3', body: 'Inter'}
};

const serifFonts = new Set(['Lora', 'Merriweather', 'EB Garamond']);

function previewFontFamily(font: string) {
    return `"${font}", ${serifFonts.has(font) ? 'Georgia, serif' : 'Arial, sans-serif'}`;
}

export function ThemeStartingPointPreview({startingPoint, preset, headingFont, bodyFont, publicationName}: {startingPoint: ThemeBaseId; preset: ThemeSetupInput['preset']; headingFont: string; bodyFont: string; publicationName: string}) {
    const layout = layouts[startingPoint];
    const colors = presets[preset || 'default'];
    const heading = headingFont || colors.heading;
    const body = bodyFont || colors.body;

    useEffect(() => {
        const families = [...new Set([heading, body])];
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = `https://fonts.googleapis.com/css2?${families.map(font => `family=${encodeURIComponent(font)}:wght@400;500;600;700`).join('&')}&display=swap`;
        document.head.append(stylesheet);
        return () => stylesheet.remove();
    }, [heading, body]);

    const style = {
        '--style-accent': colors.accent,
        '--style-background': colors.background,
        '--style-surface': colors.surface,
        '--style-text': colors.text,
        '--style-muted': colors.muted,
        '--style-heading': previewFontFamily(heading),
        '--style-body': previewFontFamily(body)
    } as CSSProperties;

    return <aside aria-label="Starting point preview" className="tb-style-preview" data-base={startingPoint} data-preset={preset || 'default'} role="region">
        <div className="tb-style-preview-heading"><strong>Starting point preview</strong><span>{layout.name} · {layout.description}</span></div>
        <div className="tb-style-sample" data-body-font={body} data-heading-font={heading} style={style}>
            <div className="tb-style-sample-nav"><strong>{publicationName.trim() || 'Your publication'}</strong><span>Home &nbsp; About</span></div>
            <div className="tb-style-sample-hero"><span>FEATURED STORY</span><h3>Stories worth slowing down for.</h3><p>A fresh perspective for curious readers.</p></div>
            <div className="tb-style-sample-posts"><article><img alt="" src={coastalImage} /><strong>The quieter side of travel</strong></article><article><img alt="" src={morningImage} /><strong>Small rituals, a bigger life</strong></article><article><img alt="" src={coastalImage} /><strong>Finding a new perspective</strong></article></div>
            <div className="tb-style-sample-action">Subscribe</div>
        </div>
        <p>Your generated theme may differ from this preview.</p>
    </aside>;
}
